import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth header
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('smbc_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('smbc_token');
      localStorage.removeItem('smbc_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  },
  setupPhonePin: async (phone, pin) => {
    const response = await api.post('/auth/setup-phone-pin', { phone, pin });
    return response.data;
  },
  getLoginStatus: async () => {
    const response = await api.get('/auth/login-status');
    return response.data;
  },
};

// Profile API
export const profileAPI = {
  get: async () => {
    const response = await api.get('/profile');
    return response.data;
  },
  update: async (updates) => {
    const response = await api.put('/profile', updates);
    return response.data;
  },
};

// Directory API
export const directoryAPI = {
  getAll: async () => {
    const response = await api.get('/directory');
    return response.data;
  },
  getMember: async (userId) => {
    const response = await api.get(`/directory/${userId}`);
    return response.data;
  },
  getMemberFull: async (userId) => {
    const response = await api.get(`/directory/${userId}/full`);
    return response.data;
  },
  updateRole: async (userId, role) => {
    const response = await api.put(`/directory/${userId}/role`, { role });
    return response.data;
  },
  updateStatus: async (userId, isActive, inactiveReason = '') => {
    const response = await api.put(`/directory/${userId}/status`, { isActive, inactiveReason });
    return response.data;
  },
};

// Announcements API
export const announcementsAPI = {
  getAll: async () => {
    const response = await api.get('/announcements');
    return response.data;
  },
  getNewCount: async () => {
    const response = await api.get('/announcements/new-count');
    return response.data;
  },
  getAllIncludingArchived: async () => {
    const response = await api.get('/announcements/all');
    return response.data;
  },
  create: async (announcement) => {
    const response = await api.post('/announcements', announcement);
    return response.data;
  },
  update: async (id, announcement) => {
    const response = await api.put(`/announcements/${id}`, announcement);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/announcements/${id}`);
    return response.data;
  },
  archive: async (id) => {
    const response = await api.post(`/announcements/${id}/archive`);
    return response.data;
  },
  unarchive: async (id) => {
    const response = await api.post(`/announcements/${id}/unarchive`);
    return response.data;
  },
  archiveGlobal: async (id) => {
    const response = await api.post(`/announcements/${id}/archive-global`);
    return response.data;
  },
  unarchiveGlobal: async (id) => {
    const response = await api.post(`/announcements/${id}/unarchive-global`);
    return response.data;
  },
  toggleReaction: async (id, emoji) => {
    const response = await api.post(`/announcements/${id}/react`, { emoji });
    return response.data;
  },
  addComment: async (id, text) => {
    const response = await api.post(`/announcements/${id}/comments`, { text });
    return response.data;
  },
  getReactionDetails: async (id) => {
    const response = await api.get(`/announcements/${id}/reaction-details`);
    return response.data;
  },
};

// Celebrations API
export const celebrationsAPI = {
  getToday: async () => {
    const response = await api.get('/celebrations/today');
    return response.data;
  },
  postWish: async (userId, message) => {
    const response = await api.post(`/celebrations/${userId}/wishes`, { message });
    return response.data;
  },
  getWishes: async (userId) => {
    const response = await api.get(`/celebrations/${userId}/wishes`);
    return response.data;
  },
};

// Upload API
export const uploadAPI = {
  uploadFile: async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minute timeout for large video uploads
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });
    return response.data;
  },
};

// Prayers API
export const prayersAPI = {
  getAll: async () => {
    const response = await api.get('/prayers');
    return response.data;
  },
  getAllIncludingArchived: async () => {
    const response = await api.get('/prayers/all');
    return response.data;
  },
  getNewCount: async () => {
    const response = await api.get('/prayers/new-count');
    return response.data;
  },
  markVisited: async () => {
    const response = await api.post('/prayers/mark-visited');
    return response.data;
  },
  create: async (request) => {
    const response = await api.post('/prayers', { request });
    return response.data;
  },
  pray: async (prayerId) => {
    const response = await api.post(`/prayers/${prayerId}/pray`);
    return response.data;
  },
  addComment: async (prayerId, text) => {
    const response = await api.post(`/prayers/${prayerId}/comments`, { text });
    return response.data;
  },
  archive: async (prayerId) => {
    const response = await api.post(`/prayers/${prayerId}/archive`);
    return response.data;
  },
  archiveGlobal: async (prayerId) => {
    const response = await api.post(`/prayers/${prayerId}/archive-global`);
    return response.data;
  },
  delete: async (prayerId) => {
    const response = await api.delete(`/prayers/${prayerId}`);
    return response.data;
  },
  toggleReaction: async (prayerId, emoji) => {
    const response = await api.post(`/prayers/${prayerId}/react`, { emoji });
    return response.data;
  },
};

// Benevolent API
export const benevolentAPI = {
  getAll: async () => {
    const response = await api.get('/benevolent');
    return response.data;
  },
  getPendingCount: async () => {
    const response = await api.get('/benevolent/pending-count');
    return response.data;
  },
  getMyUpdatesCount: async () => {
    const response = await api.get('/benevolent/my-updates-count');
    return response.data;
  },
  markViewed: async () => {
    const response = await api.post('/benevolent/mark-viewed');
    return response.data;
  },
  create: async (request) => {
    const response = await api.post('/benevolent', request);
    return response.data;
  },
  updateStatus: async (requestId, requestStatus, statusNote = '', approvedAmount = null) => {
    const payload = { requestStatus, statusNote };
    if (approvedAmount !== null) {
      payload.approvedAmount = approvedAmount;
    }
    const response = await api.put(`/benevolent/${requestId}/status`, payload);
    return response.data;
  },
  canManage: async () => {
    const response = await api.get('/benevolent/can-manage');
    return response.data;
  },
  delete: async (requestId) => {
    const response = await api.delete(`/benevolent/${requestId}`);
    return response.data;
  },
  archive: async (requestId) => {
    const response = await api.post(`/benevolent/${requestId}/archive`);
    return response.data;
  },
};

// Sermon Notes API
export const sermonNotesAPI = {
  getAll: async () => {
    const response = await api.get('/sermon-notes');
    return response.data;
  },
  create: async (note) => {
    const response = await api.post('/sermon-notes', note);
    return response.data;
  },
  update: async (noteId, note) => {
    const response = await api.put(`/sermon-notes/${noteId}`, note);
    return response.data;
  },
  delete: async (noteId) => {
    const response = await api.delete(`/sermon-notes/${noteId}`);
    return response.data;
  },
};

// Messages API
export const messagesAPI = {
  getInbox: async () => {
    const response = await api.get('/messages/inbox');
    return response.data;
  },
  send: async (message) => {
    const response = await api.post('/messages/direct', message);
    return response.data;
  },
  markAsRead: async (messageId) => {
    const response = await api.put(`/messages/${messageId}/read`);
    return response.data;
  },
  delete: async (messageId) => {
    const response = await api.delete(`/messages/${messageId}`);
    return response.data;
  },
};

// Devotional API
export const devotionalAPI = {
  get: async () => {
    const response = await api.get('/devotional');
    return response.data;
  },
};

// Groups API
export const groupsAPI = {
  getAll: async () => {
    const response = await api.get('/groups');
    return response.data;
  },
  getOne: async (groupId) => {
    const response = await api.get(`/groups/${groupId}`);
    return response.data;
  },
  requestJoin: async (groupId) => {
    const response = await api.post(`/groups/${groupId}/join`);
    return response.data;
  },
  getJoinRequests: async (groupId) => {
    const response = await api.get(`/groups/${groupId}/requests`);
    return response.data;
  },
  handleJoinRequest: async (groupId, userId, action) => {
    const response = await api.post(`/groups/${groupId}/requests/${userId}`, { action });
    return response.data;
  },
  getMembers: async (groupId) => {
    const response = await api.get(`/groups/${groupId}/members`);
    return response.data;
  },
  getMessages: async (groupId) => {
    const response = await api.get(`/groups/${groupId}/messages`);
    return response.data;
  },
  sendMessage: async (groupId, content, attachmentUrl = null, attachmentType = null, replyTo = null) => {
    const response = await api.post(`/groups/${groupId}/messages`, { 
      content, 
      attachmentUrl,
      attachmentType,
      replyTo
    });
    return response.data;
  },
  deleteMessage: async (groupId, messageId) => {
    const response = await api.delete(`/groups/${groupId}/messages/${messageId}`);
    return response.data;
  },
  toggleReaction: async (groupId, messageId, emoji) => {
    const response = await api.post(`/groups/${groupId}/messages/${messageId}/react`, { emoji });
    return response.data;
  },
  removeMember: async (groupId, userId) => {
    const response = await api.delete(`/groups/${groupId}/members/${userId}`);
    return response.data;
  },
};

// Notifications API
export const notificationsAPI = {
  getAll: async () => {
    const response = await api.get('/notifications');
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },
  markRead: async (notificationId) => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },
  markAllRead: async () => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },
  markGroupRead: async (groupId) => {
    const response = await api.put(`/notifications/group/${groupId}/read-all`);
    return response.data;
  },
  delete: async (notificationId) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },
};

// Direct Messages API
export const dmAPI = {
  getConversations: async () => {
    const response = await api.get('/dm/conversations');
    return response.data;
  },
  getConversation: async (userId) => {
    const response = await api.get(`/dm/conversation/${userId}`);
    return response.data;
  },
  sendMessage: async (userId, content, replyTo = null) => {
    const response = await api.post(`/dm/send/${userId}`, { content, replyTo });
    return response.data;
  },
  toggleReaction: async (messageId, emoji) => {
    const response = await api.post(`/dm/${messageId}/react`, { emoji });
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await api.get('/dm/unread-count');
    return response.data;
  },
  deleteConversation: async (userId) => {
    const response = await api.delete(`/dm/conversation/${userId}`);
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  createMember: async (memberData) => {
    const response = await api.post('/admin/create-member', memberData);
    return response.data;
  },
  getAllJoinRequests: async () => {
    const response = await api.get('/admin/all-join-requests');
    return response.data;
  },
  handleJoinRequest: async (groupId, userId, action) => {
    const response = await api.post(`/admin/join-requests/${groupId}/${userId}`, { action });
    return response.data;
  },
  fixLeaderAccess: async () => {
    const response = await api.post('/admin/fix-leader-memberships');
    return response.data;
  },
  getBirthdaysAnniversaries: async () => {
    const response = await api.get('/admin/birthdays-anniversaries');
    return response.data;
  },
  sendCelebrationNotifications: async () => {
    const response = await api.post('/admin/send-celebration-notifications');
    return response.data;
  },
  setGroupLeader: async (groupId, userId) => {
    const response = await api.put(`/admin/groups/${groupId}/leader`, { userId });
    return response.data;
  },
  getDuplicates: async () => {
    const response = await api.get('/admin/duplicates');
    return response.data;
  },
  mergeDuplicates: async (keepId, removeIds) => {
    const response = await api.post('/admin/merge-duplicates', { keepId, removeIds });
    return response.data;
  },
};

// Presence API (online / last seen)
export const presenceAPI = {
  heartbeat: async () => {
    const response = await api.post('/presence/heartbeat');
    return response.data;
  },
  getUser: async (userId) => {
    const response = await api.get(`/presence/${userId}`);
    return response.data;
  },
  getGroup: async (groupId) => {
    const response = await api.get(`/groups/${groupId}/presence`);
    return response.data;
  },
};

// Push Notifications API
export const pushAPI = {
  getVapidPublicKey: async () => {
    const response = await api.get('/push/vapid-public-key');
    return response.data;
  },
  subscribe: async (subscription, userAgent) => {
    const response = await api.post('/push/subscribe', { subscription, userAgent });
    return response.data;
  },
  unsubscribe: async (endpoint) => {
    const response = await api.post('/push/unsubscribe', { endpoint });
    return response.data;
  },
  testNotification: async () => {
    const response = await api.post('/push/test');
    return response.data;
  },
};

// Inspirational API
export const inspirationalAPI = {
  getPosts: async () => {
    const response = await api.get('/inspirational');
    return response.data;
  },
  getNewCount: async () => {
    const response = await api.get('/inspirational/new-count');
    return response.data;
  },
  markVisited: async () => {
    const response = await api.post('/inspirational/mark-visited');
    return response.data;
  },
  createPost: async (content, videoUrl, media = []) => {
    const response = await api.post('/inspirational', { content, videoUrl, media });
    return response.data;
  },
  toggleLike: async (postId) => {
    const response = await api.post(`/inspirational/${postId}/like`);
    return response.data;
  },
  toggleCommentLike: async (postId, commentId) => {
    const response = await api.post(`/inspirational/${postId}/comment/${commentId}/like`);
    return response.data;
  },
  addComment: async (postId, content, replyTo = null) => {
    const response = await api.post(`/inspirational/${postId}/comment`, { content, replyTo });
    return response.data;
  },
  deleteComment: async (postId, commentId) => {
    const response = await api.delete(`/inspirational/${postId}/comment/${commentId}`);
    return response.data;
  },
  deletePost: async (postId) => {
    const response = await api.delete(`/inspirational/${postId}`);
    return response.data;
  },
};

// Bible daily verse nudge
export const bibleAPI = {
  getDailyStatus: async () => {
    const response = await api.get('/bible/daily-status');
    return response.data;
  },
  markVisited: async () => {
    const response = await api.post('/bible/mark-visited');
    return response.data;
  },
};

// Live stream status
export const livestreamAPI = {
  getStatus: async () => {
    const response = await api.get('/livestream/status');
    return response.data;
  },
  toggle: async (isLive) => {
    const response = await api.post('/livestream/toggle', { isLive });
    return response.data;
  },
};

export default api;
